import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowRight, User } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { GALLERY_ITEMS, GALLERY_CATEGORIES } from "../data/gallery";
import { ARTISTS } from "../data/artists";

const artistName = (slug) => ARTISTS.find((a) => a.slug === slug)?.name || slug;

export default function Gallery() {
  const [params, setParams] = useSearchParams();
  const [category, setCategory] = useState(params.get("category") || "Mind");
  const [artist, setArtist] = useState(params.get("artist") || "");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    setCategory(params.get("category") || "Mind");
    setArtist(params.get("artist") || "");
  }, [params]);

  const update = (cat, art) => {
    const next = {};
    if (cat && cat !== "Mind") next.category = cat;
    if (art) next.artist = art;
    setParams(next, { replace: true });
  };

  const filtered = useMemo(
    () =>
      GALLERY_ITEMS.filter(
        (g) => (category === "Mind" || g.category === category) && (!artist || g.artist === artist)
      ),
    [category, artist]
  );

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setLightbox(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div data-testid="gallery-page">
      <PageHeader
        testId="gallery-header"
        motif="wave"
        eyebrow="Galéria"
        title={<>Munkáink & <span className="italic text-[#C04B58]">inspiráció.</span></>}
        description="Válogatás a stúdió munkáiból és stílusvilágából. Timi, Márk, Zsaneszka és Jaszper Andrea munkái már saját fotókkal tekinthetők meg — a többi művész képei folyamatosan érkeznek."
      />

      <section className="mx-auto max-w-7xl px-5 pb-28 sm:px-8">
        <Reveal>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2" data-testid="gallery-category-filters">
              {GALLERY_CATEGORIES.map((c) => (
                <button
                  key={c}
                  data-testid={`gallery-filter-${c.toLowerCase().replace(/[\s/]/g, "-")}`}
                  onClick={() => update(c, artist)}
                  className={`rounded-full px-4 py-2 text-xs font-semibold tracking-wide transition-all duration-300 ${
                    category === c
                      ? "bg-[#9A3542] text-white shadow-[0_0_20px_rgba(154,53,66,0.25)]"
                      : "border border-white/10 text-zinc-400 hover:border-[#9A3542] hover:text-white"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3">
              {artist && (
                <button
                  data-testid="gallery-artist-chip"
                  onClick={() => update(category, "")}
                  className="flex items-center gap-2 rounded-full border border-[#9A3542] bg-[#9A3542]/10 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#9A3542]/25"
                >
                  <User size={13} className="text-[#C04B58]" /> {artistName(artist)} <X size={13} />
                </button>
              )}
              <select
                data-testid="gallery-artist-select"
                value={artist}
                onChange={(e) => update(category, e.target.value)}
                className="rounded-full border border-[#222222] bg-[#141414] px-4 py-2 text-xs text-zinc-300 outline-none transition-colors focus:border-[#9A3542]"
              >
                <option value="">Összes művész</option>
                {ARTISTS.map((a) => (
                  <option key={a.slug} value={a.slug}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Reveal>

        <motion.div layout className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="gallery-grid">
          <AnimatePresence mode="popLayout">
            {filtered.map((g) => (
              <motion.button
                layout
                key={g.id}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.35 }}
                onClick={() => setLightbox(g)}
                data-testid={`gallery-item-${g.id}`}
                className="card-dark group relative block aspect-[4/5] overflow-hidden text-left"
              >
                <img
                  src={g.url}
                  alt={g.title}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>

        {filtered.length === 0 && (
          <p className="py-20 text-center text-sm text-zinc-500" data-testid="gallery-empty">
            Ehhez a szűréshez még nem tartozik kép — hamarosan feltöltjük.
          </p>
        )}
      </section>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-5 backdrop-blur-sm"
            onClick={() => setLightbox(null)}
          >
            <motion.div
              initial={{ scale: 0.92, y: 24 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.92, y: 24 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="card-dark relative max-h-[88vh] w-full max-w-3xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={lightbox.url} alt={lightbox.title} className="max-h-[58vh] w-full object-cover" />
              <button
                data-testid="lightbox-close"
                onClick={() => setLightbox(null)}
                className="absolute right-4 top-4 rounded-full bg-black/60 p-2 text-white transition-colors hover:bg-[#9A3542]"
                aria-label="Bezárás"
              >
                <X size={18} />
              </button>
              <div className="flex flex-wrap items-center justify-between gap-4 p-6">
                <div>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#C04B58]">{lightbox.category}</p>
                  <p className="font-display mt-1 text-2xl font-semibold text-white" data-testid="lightbox-title">{lightbox.title}</p>
                  <p className="mt-1 text-sm text-zinc-400">{artistName(lightbox.artist)}</p>
                </div>
                <Link to="/kapcsolat" data-testid="lightbox-cta" className="btn-primary !px-5 !py-2.5 !text-xs">
                  Hasonlót kérek <ArrowRight size={14} />
                </Link>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
